import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import StatusBadge from '../../components/Status/StatusBadge';
import searchIcon from '../../assets/icon-action-search-white.svg';
import TFLoading from '../../components/TFLoading';
import { SelectedAircraftWithPath } from '../InnerMenuLayoutNew';

interface AircraftlistForDropdownProps {
  aircraft: SelectedAircraftWithPath[];
  aircraftLoading: boolean;
  menuStyle?: React.CSSProperties;
  currentAircraftId: string;
}

const DropdownContent = styled.div`
  position: ${({ menuStyle }) => (menuStyle ? 'absolute' : 'relative')};
  background-color: ${({ theme }) => theme.colours.contextMenuBlue};
  min-height: ${({ menuStyle }) => (menuStyle ? '0' : '100px')};
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  background: #126fd6;
  border-color: #398fff;
`;

const LoadingContainer = styled.div`
  position: relative;
  width: 38px;
  height: 38px;
`;

const LoadingText = styled.div`
  margin-left: 50px;
  color: #fff;
  height: 100%;
  align-content: center;
  width: 150px;
`;

const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.colours.blue};
  border-color: ${({ theme }) => theme.colours.brandAccentBlueDark};
  padding: 8px 0;
  margin: 3px 8px;
  border-radius: 4px;
  width: calc(100% - 16px);
  img {
    padding: 0 8px;
    width: 32px;
  }
`;

const SearchInput = styled.input`
  background-color: ${({ theme }) => theme.colours.blue};
  border: none;
  color: ${({ theme }) => theme.colours.white};
  border-radius: 4px;
  width: calc(100% - 16px);
  &::placeholder {
    color: ${({ theme }) => theme.colours.white};
  }
  &:focus {
    outline: none;
  }
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 8px;
  gap: 8px;
  background-color: ${({ theme }) => theme.colours.contextMenuBlue};
  height: calc(100vh - 122px);
  overflow: auto;
`;

const Registration = styled.span`
  display: flex;
  align-items: center;
  width: 85px;
  color: ${({ theme }) => theme.colours.white80};
`;

const Type = styled.span`
  color: ${({ theme }) => theme.colours.innerMenuWhite};
`;

const AircraftWrapper = styled.div`
  background-color: ${({ theme }) => theme.colours.brandDarkBlue};
  color: ${({ theme }) => theme.colours.white80};
  border-radius: 4px;
  height: 38px;
  margin: 0 8px;
  padding: 8px 2px 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  &:hover {
    ${Registration} {
      color: ${({ theme }) => theme.colours.white};
    }
    ${Type} {
      color: ${({ theme }) => theme.colours.white80};
    }
  }
`;

const RegistrationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  span {
    margin-right: 8px;
    text-transform: capitalize;
  }
`;

const AircraftListForDropdown: React.FC<AircraftlistForDropdownProps> = ({
  aircraft,
  aircraftLoading,
  menuStyle,
  currentAircraftId,
}) => {
  const [localAircraft, setLocalAircraft] = useState<SelectedAircraftWithPath[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleAircraftSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    let newAircraft = aircraft;
    if (e.target.value) {
      newAircraft = aircraft.filter((ac) =>
        (ac.fleet_number + ac.registration).toLowerCase().includes(e.target.value.toLowerCase()),
      );
    }
    setLocalAircraft(newAircraft);
  };

  useEffect(() => {
    setLocalAircraft(aircraft);
  }, [aircraft]);

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  });

  const buildAircraftList = () => {
    return (
      <ListWrapper data-testid="AircraftListForDropdown--ListWrapper">
        {localAircraft
          .filter((ac) => ac.id !== currentAircraftId)
          .sort((a, b) => (a?.fleet_number + a?.registration).localeCompare(b?.fleet_number + b?.registration))
          .map((ac) => {
            const cabinIssueEnabled = ac?.standard_fields?.cabin_log?.enabled || false;
            let newPath = ac.path;
            if (!cabinIssueEnabled && newPath?.includes('cabin_log')) {
              newPath = newPath.replace('/cabin_log', '/overview');
            }
            return (
              <Link id={ac.id} to={newPath} data-testid={`AircraftListForDropdown--Link-${ac.id}`}>
                <AircraftWrapper key={ac.id} data-testid={`AircraftListForDropdown--AircraftWrapper-${ac.id}`}>
                  <RegistrationWrapper data-testid={`AircraftListForDropdown--RegistrationWrapper-${ac.id}`}>
                    <Registration data-testid={`AircraftListForDropdown--Registration-${ac.id}`}>
                      {ac.registration}
                    </Registration>
                    <Type data-testid={`AircraftListForDropdown--Type-${ac.id}`}>{ac?.aircraft_type?.designator}</Type>
                  </RegistrationWrapper>
                  <StatusWrapper>
                    <StatusBadge width="3" status={ac.maintenance_status} id={ac.id} />
                    {!menuStyle && <span>{ac.maintenance_status}</span>}
                  </StatusWrapper>
                </AircraftWrapper>
              </Link>
            );
          })}
      </ListWrapper>
    );
  };

  return (
    <DropdownContent menuStyle={menuStyle} style={menuStyle} data-testid="AircraftListForDropdown--DropdownContent">
      {aircraftLoading ? (
        <LoadingWrapper data-testid="AircraftListForDropdown--LoadingWrapper">
          <LoadingContainer>
            <TFLoading loading={aircraftLoading} contain width={22} height={22} theme="transparentWithWhite" />
            <LoadingText data-testid="AircraftListForDropdown--LoadingText">Loading aircraft...</LoadingText>
          </LoadingContainer>
        </LoadingWrapper>
      ) : (
        <>
          <SearchInputWrapper
            onClick={(e) => e.stopPropagation()}
            data-testid="AircraftListForDropdown--SearchInputWrapper"
          >
            <img src={searchIcon} alt="search" data-testid="AircraftListForDropdown--SearchIcon" />
            <SearchInput
              ref={searchInputRef}
              onChange={handleAircraftSearch}
              placeholder="Search aircraft"
              data-testid="AircraftListForDropdown--SearchInput"
            />
          </SearchInputWrapper>
          {buildAircraftList()}
        </>
      )}
    </DropdownContent>
  );
};

AircraftListForDropdown.defaultProps = {
  menuStyle: undefined,
};

export default AircraftListForDropdown;
